import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography
} from "@mui/material";
import { editSponsorshipTier } from "../../services/SponsorService";
import { SponsorshipTierListItem } from "../../data/sponsorInfo";

interface EditSponsorshipTierDialogProps {
    isOpen: boolean;
    onClose: () => void;
    tier: SponsorshipTierListItem | null;
    existingTiers: SponsorshipTierListItem[];
    onSave: () => void;
}

const EditSponsorshipTierDialog: React.FC<EditSponsorshipTierDialogProps> = ({
                                                                                 isOpen,
                                                                                 onClose,
                                                                                 tier,
                                                                                 existingTiers,
                                                                                 onSave
                                                                             }) => {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [minDonation, setMinDonation] = useState<number | "">("");
    const [maxDonation, setMaxDonation] = useState<number | "">("");
    const [sortOrder, setSortOrder] = useState<number | "">("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (tier) {
            setName(tier.Name);
            setDescription(tier.Description);
            setMinDonation(tier.MinDonation);
            setMaxDonation(tier.MaxDonation);
            setSortOrder(tier.SortOrder);
            setError(""); // Clear errors on open
        }
    }, [tier, isOpen]);

    const isOverlap = (min: number, max: number) => {
        return existingTiers.some(
            (t) =>
                t.SponsorshipTierId !== tier?.SponsorshipTierId && // Skip current tier
                ((min >= t.MinDonation && min <= t.MaxDonation) ||
                    (max >= t.MinDonation && max <= t.MaxDonation) ||
                    (min <= t.MinDonation && max >= t.MaxDonation)) // Fully overlapping range
        );
    };

    const isDuplicateName = (tierName: string) => {
        return existingTiers.some(
            (t) => t.SponsorshipTierId !== tier?.SponsorshipTierId && // Skip current tier
                t.Name.toLowerCase() === tierName.toLowerCase()
        );
    };

    const handleSave = async () => {
        if (!tier) return;
        if (!name || !description || minDonation === "" || maxDonation === "" || sortOrder === "") {
            setError("Please fill out all fields.");
            return;
        }

        if (isDuplicateName(name)) {
            setError("A sponsorship tier with this name already exists.");
            return;
        }

        if (minDonation >= maxDonation) {
            setError("Min Donation must be less than Max Donation.");
            return;
        }

        if (isOverlap(Number(minDonation), Number(maxDonation))) {
            setError("The donation range overlaps with an existing tier.");
            return;
        }

        setError(""); // Clear error before proceeding

        const updatedTier: SponsorshipTierListItem = {
            SponsorshipTierId: tier.SponsorshipTierId,  // ✅ Ensure ID is included
            Name: name,
            Description: description,
            MinDonation: Number(minDonation),
            MaxDonation: Number(maxDonation),
            SortOrder: Number(sortOrder),
            EventId: tier.EventId  // Keep event ID the same
        };

        try {
            console.log("Updating sponsorship tier:", updatedTier);
            setLoading(true);
            await editSponsorshipTier(updatedTier);

            console.log("Sponsorship tier updated successfully:", updatedTier);
            onSave();
            onClose();
        } catch (error) {
            console.error("Failed to update sponsorship tier:", error);
            alert("Failed to update sponsorship tier.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Edit Sponsorship Tier</DialogTitle>
            <DialogContent>
                <TextField
                    fullWidth
                    margin="dense"
                    label="Tier Name"
                    variant="outlined"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
                <TextField
                    fullWidth
                    margin="dense"
                    label="Description"
                    variant="outlined"
                    multiline
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />
                <TextField
                    fullWidth
                    margin="dense"
                    label="Min Donation"
                    variant="outlined"
                    type="number"
                    value={minDonation}
                    onChange={(e) => setMinDonation(e.target.value ? Number(e.target.value) : "")}
                />
                <TextField
                    fullWidth
                    margin="dense"
                    label="Max Donation"
                    variant="outlined"
                    type="number"
                    value={maxDonation}
                    onChange={(e) => setMaxDonation(e.target.value ? Number(e.target.value) : "")}
                />
                <TextField
                    fullWidth
                    margin="dense"
                    label="Sort Order"
                    variant="outlined"
                    type="number"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value ? Number(e.target.value) : "")}
                />
                {error && (
                    <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                        {error}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Cancel</Button>
                <Button onClick={handleSave} color="primary" disabled={loading}>
                    {loading ? "Saving..." : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditSponsorshipTierDialog;
